/**
 * Rig world-convention math (ADR-003 §2b: declared upAxis/facing) — the ONE
 * place the declared conventions become geometry. Two consumers:
 *
 * 1. The hub→rig-world triad (canonical-secondary transport, §11).
 * 2. The source→target convention rotation for FACING/UP NORMALIZATION —
 *    root-motion excursions live in WORLD space, so a −Z-facing source's
 *    "walk forward" would push a +Z-facing target backwards. The correction
 *    is a RIGID rotation of root excursions by the declared-convention map —
 *    never patched into binds/boneInverses, which double-rotates deltas and
 *    twists hips (the Gen-2 scar, research digest §2.5). v2 had to MEASURE
 *    facing from the hips via swing-twist; v3 declares it, validated, so the
 *    map is deterministic.
 *
 * Depends on: math/quat, math/vec3, ir/rig-descriptor (types).
 */
import {
  quatFromAxisAngle,
  quatMultiply,
  quatNormalize,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import { vec3Cross, vec3Dot, type Vec3Data } from '../math/vec3.js';
import type { RigDescriptor } from '../ir/rig-descriptor.js';

export interface ConventionTriad {
  readonly side: Vec3Data;
  readonly up: Vec3Data;
  readonly facing: Vec3Data;
}

const FACING_VECTORS: Record<RigDescriptor['facing'], Vec3Data> = {
  '+X': { x: 1, y: 0, z: 0 },
  '-X': { x: -1, y: 0, z: 0 },
  '+Z': { x: 0, y: 0, z: 1 },
  '-Z': { x: 0, y: 0, z: -1 },
};

/**
 * Exact axis-aligned vector → facing label. Null for anything that is not
 * one of the four schema facings (callers hand in cross products of triad
 * axes, which are exact ±1 components — a null here is a caller bug).
 */
export function facingFromVector(v: Vec3Data): RigDescriptor['facing'] | null {
  for (const [label, axis] of Object.entries(FACING_VECTORS) as Array<
    [RigDescriptor['facing'], Vec3Data]
  >) {
    if (Math.abs(v.x - axis.x) < 1e-9 && Math.abs(v.y - axis.y) < 1e-9 && Math.abs(v.z - axis.z) < 1e-9) {
      return label;
    }
  }
  return null;
}

/**
 * The rig's declared right-handed world triad (side = up × facing, matching
 * the hub's X = Y × Z). Null for a degenerate declaration (up ∥ facing) —
 * parseRigDescriptor rejects those at the door, so null here only defends
 * against descriptors constructed without going through the door.
 */
export function conventionTriad(rig: Pick<RigDescriptor, 'upAxis' | 'facing'>): ConventionTriad | null {
  const up: Vec3Data = rig.upAxis === 'Y' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
  const facing = FACING_VECTORS[rig.facing];
  if (Math.abs(vec3Dot(up, facing)) > 1e-9) return null;
  return { side: vec3Cross(up, facing), up, facing };
}

/** Hub axes (+Y up, +Z forward, ADR-003 §2a) → this rig's world axes. */
export function hubToWorld(rig: RigDescriptor): ((v: Vec3Data) => Vec3Data) | null {
  const t = conventionTriad(rig);
  if (t === null) return null;
  return (v) => ({
    x: t.side.x * v.x + t.up.x * v.y + t.facing.x * v.z,
    y: t.side.y * v.x + t.up.y * v.y + t.facing.y * v.z,
    z: t.side.z * v.x + t.up.z * v.y + t.facing.z * v.z,
  });
}

/**
 * The rotation carrying the SOURCE rig's declared world convention onto the
 * TARGET's: Q·up_s = up_t, Q·facing_s = facing_t (side follows by
 * handedness). Built as up-alignment followed by a signed yaw about the
 * target's up — both steps exact for the schema's axis-aligned conventions.
 * Returns null when the conventions already agree (the common case — a
 * deliberate "no correction", distinguishable from an identity rotation).
 */
export function conventionRotation(source: RigDescriptor, target: RigDescriptor): Quat | null {
  if (source.upAxis === target.upAxis && source.facing === target.facing) return null;
  const s = conventionTriad(source);
  const t = conventionTriad(target);
  if (s === null || t === null) return null; // degenerate — rejected at parse; defend anyway
  // Up alignment: identical or an exact Y↔Z quarter turn (never antiparallel —
  // the schema's up axes are both positive).
  let q: Quat = { x: 0, y: 0, z: 0, w: 1 };
  const upDot = vec3Dot(s.up, t.up);
  if (upDot < 1 - 1e-12) {
    const axis = vec3Cross(s.up, t.up);
    q = quatFromAxisAngle(axis, Math.acos(Math.max(-1, Math.min(1, upDot))));
  }
  // Yaw about the target's up: signed angle from the transported source
  // facing to the target facing (both ⊥ up_t by construction).
  const f = quatRotateVec3(q, s.facing);
  const cos = vec3Dot(f, t.facing);
  const sin = vec3Dot(t.up, vec3Cross(f, t.facing));
  const yaw = Math.atan2(sin, cos);
  if (Math.abs(yaw) > 1e-12) {
    q = quatMultiply(quatFromAxisAngle(t.up, yaw), q);
  }
  return quatNormalize(q);
}
